import { app, state, v } from "modular-openscriptjs";
import { tool } from "../../fotastart";
import { isMobile } from "../../helpers";
import { api } from "../../declarations";
import ModalBody from "./ModalBody";
import OffcanvasBody from "../Offcanvas/OffcanvasBody";

const h = app("h");

/**
 * Opens a view-only modal/offcanvas showing the customer's own file
 * attached to a choice_type = file order item feature (e.g. a
 * prescription they uploaded) - decrypted server-side by
 * FeatureFileController, shown here via a short-lived blob: URL.
 *
 * No download/save action is offered - same reasoning as
 * business.carata's copy of this component (see its docblock): this
 * stays sensitive data even on-screen, so it's only ever rendered,
 * never linked to, and the blob URL is revoked as soon as this closes.
 *
 * @param {{id: number|string, feature_name?: string}} feature - an
 *   OrderItemFeatures row (feature_name for the title).
 */
export function openOrderFileModal(feature) {
    const status = state("loading"); // loading | image | pdf | error
    let objectUrl = null;

    const releaseObjectUrl = () => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
    };

    const body = () =>
        h.div(
            { class: "p-2" },
            h.div(
                {
                    class: "d-flex justify-content-between align-items-center mb-4",
                },
                h.h5(
                    { class: "fw-bold mb-0" },
                    feature.feature_name || "Attached File",
                ),
                h.button({
                    class: "btn-close",
                    listeners: { click: () => holder.hide() },
                }),
            ),

            v(status, (s) => {
                if (s.value === "error") {
                    return h.div(
                        { class: "text-center text-muted py-5" },
                        h.i({
                            class: "bi bi-exclamation-triangle fs-1 d-block mb-2",
                        }),
                        "Unable to load this file.",
                    );
                }

                if (s.value === "pdf") {
                    return h.iframe({
                        src: objectUrl,
                        style: "width: 100%; height: 70vh; border: 0;",
                    });
                }

                if (s.value === "image") {
                    return h.div(
                        { class: "text-center" },
                        h.img({
                            src: objectUrl,
                            style: "max-width: 100%; max-height: 70vh; border-radius: 12px;",
                        }),
                    );
                }

                return h.div(
                    { class: "d-flex justify-content-center py-5" },
                    // Inlined rather than h.SmallSpinner() - that registered-
                    // component call only resolves correctly from inside an
                    // active Component render pass. This function is invoked
                    // from a plain click listener (long after any render has
                    // finished), where h.SmallSpinner turned out to be the raw
                    // class rather than an invocable factory ("Class constructor
                    // SmallSpinner cannot be invoked without 'new'").
                    h.div(
                        { class: "d-flex justify-content-center align-items-center" },
                        h.div({ class: "spinner-border spinner-border-sm" }),
                    ),
                );
            }),

            h.small(
                { class: "text-muted d-block mt-3 text-center" },
                "View only - this file can't be downloaded or saved from here.",
            ),
        );

    let holder;
    if (isMobile()) {
        holder = tool.offcanvas(h.OffcanvasBody(body()), null, "bottom", {
            class: "rounded-top-5",
        });
    } else {
        holder = tool.plainModal(h.ModalBody(body()));
    }

    holder._element?.addEventListener("hidden.bs.modal", releaseObjectUrl, {
        once: true,
    });
    holder._element?.addEventListener(
        "hidden.bs.offcanvas",
        releaseObjectUrl,
        { once: true },
    );

    holder.show();

    fetch(`${api.baseUrl}/order-item-features/${feature.id}/file`, {
        headers: {
            Authorization: `Bearer ${api.token}`,
            Accept: "*/*",
        },
    })
        .then(async (res) => {
            if (!res.ok) {
                throw new Error(`request failed with status ${res.status}`);
            }

            const contentType = res.headers.get("content-type") || "";
            const blob = await res.blob();
            objectUrl = URL.createObjectURL(blob);
            status.value = contentType.includes("pdf") ? "pdf" : "image";
        })
        .catch(() => {
            status.value = "error";
        });
}
